/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Backdrop, Modal as MuiModal, Paper } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import get from 'lodash-es/get';
import React, { DependencyList } from 'react';
import { useModal as useReactModalHook } from 'react-modal-hook';

interface ModalOptions {
  clickOutsideCloses?: boolean;
  escapeCloses?: boolean;
  /**
   * Called when the user dismisses the modal with escape or a click outside —
   * not when the owner closes it via hideModal(). Callers that hand out a
   * promise need this to settle it; without it a dismissal is indistinguishable
   * from the modal never having been answered.
   */
  onDismiss?: () => void;
}

// deps sit second, like useMemo, so react-hooks/exhaustive-deps (additionalHooks)
// can check them; react-modal-hook freezes the modal on them.
function useModal(jsxCallback: () => React.ReactNode, deps: DependencyList, options: ModalOptions = {}) {
  const clickOutsideCloses = get(options, 'clickOutsideCloses', false);
  const escapeCloses = get(options, 'escapeCloses', true);
  const onDismiss = get(options, 'onDismiss', null);

  const [showModal, hideModal] = useReactModalHook(
    () => (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <MuiModal
          slots={{ backdrop: Backdrop }}
          slotProps={{
            backdrop: {
              onClick: (e) => {
                e.stopPropagation();
                if (clickOutsideCloses) {
                  if (onDismiss) onDismiss();
                  hideModal();
                }
              },
            },
          }}
          open
          onClose={(e, reason) => {
            if (reason !== 'escapeKeyDown' || !escapeCloses) return;
            if (onDismiss) onDismiss();
            hideModal();
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', height: '100%' }}>
            <Paper
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                minWidth: 480,
                maxWidth: '90vw',
                maxHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
              }}
            >
              {jsxCallback()}
            </Paper>
          </div>
        </MuiModal>
      </LocalizationProvider>
    ),
    deps
  );

  return [showModal, hideModal];
}

export default useModal;
